// Roles as the API's plain ids: display labels, what each role grants and implies (the API's IMPLIED_ROLES), the highest-role label and effective roles of an assignment.
import type { Role } from "@/api/__generated__";

export const ROLES: readonly Role[] = ["reader", "licensed", "confidential", "approver", "operator", "admin"];

export const ROLE_LABELS: Record<Role, string> = {
  reader: "Reader",
  licensed: "Licensed",
  confidential: "Confidential",
  approver: "Approver",
  operator: "Operator",
  admin: "Admin",
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  reader: "Reads public grid codes: requirements, library, curves, glossary, what changed and the assistant.",
  licensed: "Also reads licensed standards and documents.",
  confidential:
    "Also sees confidential documents and compliance assessments: uploads capability documents, runs assessments and reviews their topics.",
  approver: "Signs off compliance assessments once every topic is reviewed, and reopens them.",
  operator:
    "Runs ingestion, source checks and version lifecycle jobs, and sees extraction quality, parameters, runs and sources.",
  admin: "Every role, plus the system page and user administration: invitations, roles and the security log.",
};

export const IMPLIED_ROLES: Record<Role, readonly Role[]> = {
  reader: [],
  licensed: ["reader"],
  confidential: ["reader"],
  approver: ["reader", "confidential"],
  operator: ["reader"],
  admin: ["reader", "licensed", "confidential", "approver", "operator"],
};

const ROLE_RANK: readonly Role[] = ["admin", "operator", "approver", "confidential", "licensed", "reader"];

export function primaryRoleLabel(roles: readonly Role[]): string {
  const top = ROLE_RANK.find((role) => roles.includes(role));
  return top === undefined ? "No role" : ROLE_LABELS[top];
}

export function effectiveRoles(assigned: readonly Role[]): Role[] {
  const all = new Set<Role>();
  for (const role of assigned) {
    all.add(role);
    for (const implied of IMPLIED_ROLES[role]) {
      all.add(implied);
    }
  }
  return ROLES.filter((role) => all.has(role));
}

export function sortRoles(roles: readonly Role[]): Role[] {
  return ROLES.filter((role) => roles.includes(role));
}
